<template>
  <FormField :label="label" :error="error" :hint="hint" :id="id" v-slot="{ id: fieldId, invalid, describedBy }">
    <div :class="$slots.suffix || $slots.prefix ? 'relative' : undefined">
      <!-- Left-side adornment (search icon etc.) -->
      <span
        v-if="$slots.prefix"
        class="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none flex items-center"
        style="color: var(--text-tertiary); font-size: var(--text-sm);"
      >
        <slot name="prefix" />
      </span>
      <input
        :id="fieldId"
        class="input"
        :class="[$slots.suffix && 'has-suffix', $slots.prefix && 'has-prefix']"
        :value="decimal ? decimalText : modelValue"
        :inputmode="decimal ? 'decimal' : undefined"
        :aria-invalid="invalid || undefined"
        :aria-describedby="describedBy"
        v-bind="$attrs"
        @input="onInput"
      />
      <!-- Right-side adornment (currency symbol etc.) -->
      <span
        v-if="$slots.suffix"
        class="absolute right-4 top-1/2 -translate-y-1/2"
        style="color: var(--text-tertiary); font-size: var(--text-sm);"
      >
        <slot name="suffix" />
      </span>
    </div>
  </FormField>
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import FormField from "./FormField.vue"

/**
 * Standard text input with the label/error/hint triad built in. Wraps the
 * global .input class — never restyle an input inline. All native attributes
 * (type, placeholder, autocomplete, inputmode, min/max/step, disabled, …)
 * fall through to the <input>; number coercion follows the input type.
 */
defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null
    label?: string
    error?: string
    hint?: string
    id?: string
    /** Coerce v-model value to number (for type="number" inputs). */
    number?: boolean
    /**
     * Locale-tolerant money/decimal entry: a text input with the decimal
     * numpad that accepts both "," and "." as the decimal separator (native
     * type="number" silently rejects "199,50"). v-model is number | null —
     * null while empty/unparseable, so placeholders show instead of a "0".
     */
    decimal?: boolean
  }>(),
  { modelValue: "" },
)

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void
}>()

// Decimal mode keeps the raw typed text ("12," mid-entry) so parsing never
// fights the user's caret; the model only ever sees number | null.
const decimalText = ref(props.modelValue === null || props.modelValue === "" ? "" : String(props.modelValue))

watch(
  () => props.modelValue,
  (value) => {
    if (!props.decimal) return
    const parsed = parseDecimal(decimalText.value)
    // Only overwrite the user's text when the model changed externally
    // (form reset, AI prefill) — not as an echo of their own typing.
    if (value === parsed) return
    decimalText.value = value === null || value === "" || value === undefined ? "" : String(value)
  },
)

function parseDecimal(raw: string): number | null {
  const normalized = raw.trim().replace(/\s/g, "").replace(",", ".")
  if (normalized === "") return null
  const n = Number(normalized)
  return Number.isFinite(n) ? n : null
}

function onInput(event: Event) {
  const el = event.target as HTMLInputElement
  if (props.decimal) {
    decimalText.value = el.value
    emit("update:modelValue", parseDecimal(el.value))
  } else if (props.number) {
    // Mirror v-model.number: empty/invalid input stays as-is (empty string)
    emit("update:modelValue", el.value === "" || Number.isNaN(el.valueAsNumber) ? el.value : el.valueAsNumber)
  } else {
    emit("update:modelValue", el.value)
  }
}
</script>
